import { useEffect, useState, useCallback } from "react";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import parentApi from "@/lib/parentApi";
import ChatThread from "@/components/ChatThread";
import { formatApiErrorDetail } from "@/lib/api";
import { useUrlState } from "@/lib/useUrlState";

export function StudentMessages({ studentId }) {
  const [data, setData] = useState(null);
  const [teacher, setTeacher] = useUrlState("teacher", "");

  const load = useCallback(async () => {
    try {
      const { data: d } = await parentApi.get(`/parent/threads/${studentId}`, { params: { teacher_id: teacher || undefined } });
      setData(d);
      if (!teacher && d.conversation) setTeacher(d.conversation);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load messages"); }
  }, [studentId, teacher, setTeacher]);
  useEffect(() => { setData(null); load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);

  const send = async (body, attachment) => {
    try {
      const { data: msg } = await parentApi.post(`/parent/threads/${studentId}`, { body, attachment: attachment || undefined, to_teacher_id: teacher || data?.conversation });
      setData((d) => ({ ...d, messages: [...(d?.messages || []), msg] }));
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed"); return false; }
  };

  const active = (data?.teachers || []).find((t) => t.id === (teacher || data?.conversation));
  return (
    <section className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col flex-1 min-h-[380px]" data-testid="parent-student-messages">
      {(data?.teachers || []).length > 0 && (
        <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2 text-xs" data-testid="parent-message-teacher-picker">
          <span className="text-slate-500 shrink-0">Conversation with</span>
          <div className="flex gap-1.5 overflow-x-auto">
            {data.teachers.map((t) => {
              const on = t.id === (teacher || data.conversation);
              return (
                <button key={t.id} onClick={() => setTeacher(t.id)} data-testid={`parent-msg-to-${t.id}`} title={t.class_name}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium ${on ? "bg-primary text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                  {t.name}<span className="opacity-60 font-normal"> · {t.homeroom ? "Homeroom" : t.class_name}</span>
                  {t.unread > 0 && <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white" data-testid={`parent-msg-unread-${t.id}`}>{t.unread}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="px-4 py-2 border-b border-slate-100 text-[11px] text-slate-500 inline-flex items-center gap-1.5" data-testid="parent-msg-privacy">
        <Lock className="h-3 w-3 text-slate-400" />
        {data === null ? "…" : active ? `Private conversation with ${active.name} — only ${active.id === "__admin__" ? "the school office" : active.name} and the school office can read it.` : "No teacher assigned yet — ask the school office."}
      </div>
      <div className="flex-1 min-h-0">
        {data === null ? <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          : <ChatThread key={teacher || data.conversation || "none"} messages={data.messages || []} onSend={send} mySenderType="parent" emptyText={active ? `Start the conversation with ${active.name}.` : "No teacher to message yet."} />}
      </div>
    </section>
  );
}
